'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useApprovalNotifications } from '@/hooks/useReview'
import { approvalsApi } from '@/lib/ap/review'
import type { ReviewNotification } from '@/lib/ap/review'
import { workbenchHref } from '@/lib/ap/invoices'
import { announceReviewUpdated, badgeText, relativeAge } from '@/lib/review-view'
import { canReadApprovalNotifications } from '@/lib/roles'
import { cn } from '@/lib/utils'

/**
 * The header bell (A4): decisions reviewers made on the approval rounds the
 * signed-in AP user sent (`GET /api/ap/approval-notifications`), unread until
 * opened. Opening one marks it seen and goes to the invoice in the Workbench.
 * Roles that cannot request approvals see an honest empty state.
 */
export function NotificationCenter() {
  const router = useRouter()
  const { data: session } = useSession()
  const allowed = canReadApprovalNotifications(session?.roles)
  const [open, setOpen] = useState(false)
  const list = useApprovalNotifications(allowed)
  const items = list.data?.items ?? []
  const unread = list.data?.unread ?? 0
  const badge = badgeText(unread)

  const markSeen = async (ids?: number[]) => {
    try {
      await approvalsApi.markSeen(ids)
      announceReviewUpdated()
    } catch {
      // Best effort: the badge stays until the next refresh.
    }
    void list.refresh()
  }

  const openItem = (item: ReviewNotification) => {
    setOpen(false)
    if (item.assignment_id != null && item.unread) void markSeen([item.assignment_id])
    if (item.invoice_db_id != null) router.push(workbenchHref(item.invoice_db_id, 'all'))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          aria-label={badge ? `Notifications, ${badge} unread` : 'Notifications'}
          className='relative rounded-full text-muted-foreground hover:text-foreground'
        >
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
          {badge && (
            <span
              aria-hidden='true'
              className='absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground'
            >
              {badge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <span className='sr-only' role='status' aria-live='polite'>
        {badge ? `${badge} unread notifications` : ''}
      </span>
      <PopoverContent align='end' className='w-80 p-0'>
        <div className='flex items-center justify-between border-b border-border/50 px-4 py-3'>
          <h2 className='font-display text-sm font-semibold text-foreground'>Notifications</h2>
          {unread > 0 && (
            <Button
              variant='link'
              size='sm'
              onClick={() => void markSeen()}
              className='h-auto p-0 text-xs text-brand-purple'
            >
              Mark all as seen
            </Button>
          )}
        </div>
        <div className='max-h-80 overflow-y-auto' aria-busy={allowed && list.loading}>
          {!allowed ? (
            <EmptyState text='Approval decisions on invoices you send for approval appear here.' />
          ) : !list.data && !list.error ? (
            <ul className='divide-y divide-border/50' aria-label='Loading notifications'>
              {[0, 1, 2].map((i) => (
                <li key={i} className='flex gap-3 px-4 py-3'>
                  <Skeleton variant='circular' className='mt-1.5 h-2 w-2' />
                  <div className='flex-1 space-y-1.5'>
                    <Skeleton variant='text' className='h-3.5 w-3/4' />
                    <Skeleton variant='text' className='h-3 w-1/2' />
                  </div>
                </li>
              ))}
            </ul>
          ) : list.error && !list.data ? (
            <div className='space-y-2 px-4 py-6 text-center text-sm'>
              <p className='text-danger-strong'>Could not load notifications.</p>
              <Button variant='link' size='sm' onClick={() => void list.refresh()} className='h-auto p-0 text-xs'>
                Retry
              </Button>
            </div>
          ) : items.length === 0 ? (
            <EmptyState text='No approval decisions yet. When a reviewer approves, rejects or asks about an invoice you sent, it appears here.' />
          ) : (
            <ul className='divide-y divide-border/50'>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type='button'
                    onClick={() => openItem(item)}
                    className={cn(
                      'flex w-full gap-3 px-4 py-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      item.unread && 'bg-brand-cornflower/10'
                    )}
                  >
                    <span
                      aria-hidden='true'
                      className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', item.unread ? 'bg-brand-purple' : 'bg-transparent')}
                    />
                    <span className='min-w-0 flex-1'>
                      <span className='block text-sm font-medium text-foreground'>
                        {item.title}
                        {item.unread && <span className='sr-only'> (unread)</span>}
                      </span>
                      {(item.invoice_number || item.vendor_name) && (
                        <span className='mt-0.5 block truncate text-xs text-muted-foreground'>
                          {[item.invoice_number, item.vendor_name].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {item.created_at && (
                        <span className='mt-0.5 block text-xs text-muted-foreground'>{relativeAge(item.created_at)}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className='px-4 py-8 text-center'>
      <div className='mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted/50'>
        <Icons.bell className='h-5 w-5 text-muted-foreground' strokeWidth={1.5} aria-hidden='true' />
      </div>
      <p className='text-sm font-medium text-foreground'>No notifications</p>
      <p className='mt-1 text-xs text-muted-foreground'>{text}</p>
    </div>
  )
}
